import React, { useState } from 'react'

import { useTranslation } from 'language/translator'

import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'
import Grid from 'components_new/atoms/Grid'

import CardOption from 'components_new/molecules/CardOption'
import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import StatusBox from 'components_new/molecules/StatusBox'

import EntitySlices from 'components_new/organisms/EntitySlices'

import { ISelectMetric } from 'types/GlobalMetric'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'

import type { Step } from '../lib'

interface Props {
  step: Step
  metric: ISelectMetric
  slices: string[]
  setSlices: (v: string[]) => void
}

const SlicesStep = ({ step, metric, slices, setSlices }: Props) => {
  const { t } = useTranslation()
  const { isAdmin } = useAccountSettings()

  const [editSlices, setEditSlices] = useState(false)

  const singleRequired = metric.slices.selection_mode === 'single_required'

  const handleToggle = (id: string) => {
    if (singleRequired) {
      if (slices[0] === id) return

      setSlices([id])

      return
    }

    if (slices.includes(id)) {
      setSlices(slices.filter((s) => s !== id))
    } else {
      setSlices([...slices, id])
    }
  }

  return (
    <Collapse in={step === 'slices'}>
      <Collapse in={!singleRequired && slices.length > 1}>
        <StatusBox severity="info" sx={{ mb: 2 }} allowClose={false}>
          <b>{t('global', 'note')}</b>{' '}
          {t('entities', 'multiple_slices_warning_body')}
        </StatusBox>
      </Collapse>

      {metric.slices.available.length === 0 ? (
        <EmptyPlaceholder
          actions={
            isAdmin && metric.entity_id ? (
              <Button onClick={() => setEditSlices(true)}>
                {t('entities', 'edit_slices')}
              </Button>
            ) : null
          }
          background={true}
          height={200}
          size="small"
          title={t('global', 'no_options_found')}
        />
      ) : (
        <Grid container={true} spacing={1}>
          {singleRequired ? null : (
            <Grid item={true} xs={4}>
              <CardOption
                id={null}
                title={t('global', 'none')}
                active={slices.length === 0}
                onChange={() => setSlices([])}
                sx={{ height: '100%', opacity: 0.5 }}
              />
            </Grid>
          )}
          {metric.slices.available.map((s) => {
            const isSelected = slices.includes(s.id)

            const isSupported =
              s.supported_fact_table_ids?.includes(metric.fact_table_id) ?? true

            // an unsupported slice that is already selected stays
            // interactive so that it can be switched off
            const isUnsupported = !isSupported && !isSelected

            return (
              <Grid item={true} key={s.id} xs={4}>
                <CardOption
                  id={s.id}
                  active={isSelected}
                  title={s.name}
                  body={
                    !s.standard
                      ? `${t('global', 'custom')} ${t(
                        'entities',
                        'slice',
                        true
                      )}`
                      : `${t('global', 'standard')} ${t(
                        'entities',
                        'slice',
                        true
                      )}`
                  }
                  disabled={isUnsupported}
                  disabledText={t('entities', 'slice_not_supported_by_metric')}
                  multiple={!singleRequired}
                  onChange={() => handleToggle(s.id)}
                  sx={{ height: '100%' }}
                />
              </Grid>
            )
          })}
          {isAdmin && metric.entity_id ? (
            <Grid item={true} xs={4}>
              <EmptyPlaceholder
                actions={
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setEditSlices(true)}
                  >
                    {t('entities', 'edit_slices')}
                  </Button>
                }
                background={true}
              />
            </Grid>
          ) : null}
        </Grid>
      )}

      {/*-- dialogs --*/}
      {metric.entity_id ? (
        <SimpleDialog
          actions={
            <Button onClick={() => setEditSlices(false)} variant="text">
              {t('global', 'close')}
            </Button>
          }
          onClose={() => setEditSlices(false)}
          open={editSlices}
          sx={{
            zIndex: 1302 // for fullSize
          }}
          title={t('entities', 'slices')}
        >
          <EntitySlices edit={true} id={metric.entity_id} />
        </SimpleDialog>
      ) : null}
    </Collapse>
  )
}

export default SlicesStep
